import { useEffect, useRef } from 'react';
import { useViewStore } from '@/stores/viewStore.js';
import { useParamsStore } from '@/stores/paramsStore.js';
import type { Heightmap } from '@wg/noise';
import { paintHeightmapInto } from './render.js';

export function Viewer2D({ heightmap }: { heightmap: Heightmap | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const twoDMode = useViewStore((s) => s.twoDMode);
  const paletteId = useViewStore((s) => s.paletteId);
  const waterLevel = useParamsStore((s) => s.params.waterLevel);

  useEffect(() => {
    if (!heightmap || !ref.current) return;
    const canvas = ref.current;
    const n = heightmap.resolution;
    // Setting width/height clears the backing store. Skip the assignment when
    // the resolution hasn't actually changed (common across palette/mode toggles).
    if (canvas.width !== n) canvas.width = n;
    if (canvas.height !== n) canvas.height = n;
    const ctx = canvas.getContext('2d', { willReadFrequently: false })!;
    const imageData = ctx.createImageData(n, n);
    paintHeightmapInto(imageData, heightmap.data, n, twoDMode, paletteId, waterLevel);
    ctx.putImageData(imageData, 0, 0);
  }, [heightmap, twoDMode, paletteId, waterLevel]);

  return (
    <canvas
      ref={ref}
      className="h-full w-full object-contain [image-rendering:pixelated]"
      aria-label={`Heightmap rendered as ${twoDMode}`}
    />
  );
}
